import { useEffect, useState, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ReloadPage from "../utils/func/ReloadPage";

const JSON_URL = "https://raw.githubusercontent.com/unaivan22/openAssets/refs/heads/master/gnr_kode_rekening90.json";

export default function KodeRekening() {
  const [data, setData] = useState([]);
  const [parentCodes, setParentCodes] = useState(new Set());

  const [jenis, setJenis] = useState([]);
  const [objek, setObjek] = useState([]);
  const [rincian, setRincian] = useState([]);
  const [subrincian, setSubrincian] = useState([]);
  const [subsubrincian, setSubsubrincian] = useState([]);
  const [barang, setBarang] = useState([]);

  const [selectedJenis, setSelectedJenis] = useState("");
  const [selectedObjek, setSelectedObjek] = useState("");
  const [selectedRincian, setSelectedRincian] = useState("");
  const [selectedSubrincian, setSelectedSubrincian] = useState("");
  const [selectedSubsubrincian, setSelectedSubsubrincian] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 1000;

  const [loading, setLoading] = useState(true);

  // LOAD JSON
  useEffect(() => {
    setLoading(true);
    fetch(JSON_URL)
      .then((res) => res.json())
      .then((json) => {
        setData(json || []);

        // --- Hitung parent codes (pakai kode)
        const parents = new Set();
        json.forEach((it) => {
          const kode = it.kode;
          if (!kode) return;
          const parts = kode.split(".");
          for (let i = 1; i < parts.length; i++) {
            parents.add(parts.slice(0, i).join("."));
          }
        });

        setParentCodes(parents);

        // --- Jenis = level 2 (misal 1.1)
        const jenisData = json.filter((i) => i.kode && i.kode.split(".").length === 2);
        setJenis(jenisData);
      })
      .finally(() => setLoading(false));
  }, []);

  // helper
  const immediateChildren = (prefix) => {
    const level = prefix.split(".").length;
    return data.filter(
      (i) =>
        i.kode &&
        i.kode.startsWith(prefix + ".") &&
        i.kode.split(".").length === level + 1
    );
  };

  const allDescendants = (prefix) =>
    data.filter((i) => i.kode && i.kode.startsWith(prefix + "."));

  // --- Jenis Selected ---
  useEffect(() => {
    setSelectedObjek("");
    setSelectedRincian("");
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setObjek([]);
    setRincian([]);
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedJenis) {
      setObjek(immediateChildren(selectedJenis));
    }
  }, [selectedJenis, data]);

  // --- Objek Selected ---
  useEffect(() => {
    setSelectedRincian("");
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setRincian([]);
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedObjek) {
      setRincian(immediateChildren(selectedObjek));

      const desc = allDescendants(selectedObjek);
      setBarang(desc.filter((i) => !parentCodes.has(i.kode)));
    }
  }, [selectedObjek, data, parentCodes]);

  // --- Rincian Selected ---
  useEffect(() => {
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedRincian) {
      setSubrincian(immediateChildren(selectedRincian));

      const desc = allDescendants(selectedRincian);
      setBarang(desc.filter((i) => !parentCodes.has(i.kode)));
    }
  }, [selectedRincian, data, parentCodes]);

  // --- SubRincian Selected ---
  useEffect(() => {
    setSelectedSubsubrincian("");
    setSubsubrincian([]);
    setBarang([]);

    if (selectedSubrincian) {
      setSubsubrincian(immediateChildren(selectedSubrincian));

      const desc = allDescendants(selectedSubrincian);
      setBarang(desc.filter((i) => !parentCodes.has(i.kode)));
    }
  }, [selectedSubrincian, data, parentCodes]);

  // --- SubSubRincian Selected ---
  useEffect(() => {
    setBarang([]);

    if (selectedSubsubrincian) {
      const desc = allDescendants(selectedSubsubrincian);
      setBarang(desc.filter((i) => !parentCodes.has(i.kode)));
    }
  }, [selectedSubsubrincian, data, parentCodes]);

  // SEARCH
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return barang.filter(
      (b) =>
        b.kode.toLowerCase().includes(q) ||
        b.nama.toLowerCase().includes(q)
    );
  }, [barang, search]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Memuat data rekening, harap tunggu hingga selesai...</p>
        </div>
      </div>;
  }

  return (
    <div>
        {/* FILTER */}
        <div className="flex flex-col gap-4 border p-4 rounded-2xl bg-stone-100">

          {/* Jenis */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Jenis</p>
            <Select onValueChange={setSelectedJenis}>
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Jenis Aset" />
              </SelectTrigger>
              <SelectContent>
                {jenis.map((j) => (
                  <SelectItem key={j.kode} value={j.kode} className="text-xs">
                    {j.kode} - {j.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Objek */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Objek</p>
            <Select onValueChange={setSelectedObjek} disabled={!objek.length}>
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Objek" />
              </SelectTrigger>
              <SelectContent>
                {objek.map((o) => (
                  <SelectItem key={o.kode} value={o.kode} className="text-xs">
                    {o.kode} - {o.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rincian */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Rincian</p>
            <Select onValueChange={setSelectedRincian} disabled={!rincian.length}>
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Rincian" />
              </SelectTrigger>
              <SelectContent>
                {rincian.map((r) => (
                  <SelectItem key={r.kode} value={r.kode} className="text-xs">
                    {r.kode} - {r.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sub Rincian */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Sub Rincian</p>
            <Select
              onValueChange={setSelectedSubrincian}
              disabled={!subrincian.length}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Sub Rincian" />
              </SelectTrigger>
              <SelectContent>
                {subrincian.map((sr) => (
                  <SelectItem key={sr.kode} value={sr.kode} className="text-xs">
                    {sr.kode} - {sr.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sub Sub Rincian */}
          {/* <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Sub Sub Rincian</p>
            <Select
              onValueChange={setSelectedSubsubrincian}
              disabled={!subsubrincian.length}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Sub Sub Rincian" />
              </SelectTrigger>
              <SelectContent>
                {subsubrincian.map((ssr) => (
                  <SelectItem key={ssr.kode} value={ssr.kode} className="text-xs">
                    {ssr.kode} - {ssr.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}

          <ReloadPage />
          
        </div>

        {/* Search */}
        <Input
          type="search"
          placeholder="Cari kode rekening / nama rekening..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='my-4 shadow-xl'
        />

        {/* Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Kode Rekening</TableHead>
              <TableHead className="text-xs">Nama Rekening</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.map((b, idx) => (
              <TableRow key={idx}>
                <TableCell className="text-xs">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="cursor-help">{b.kode}</span>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        {(() => {
                          if (!b?.kode) return "Tidak ada detail";

                          const kodeParts = b.kode.split(".");
                          const path = [];
                          for (let i = 1; i <= kodeParts.length; i++) {
                            const prefix = kodeParts.slice(0, i).join(".");
                            const found = data.find((d) => d.kode === prefix);
                            if (found && found.nama) path.push(found.nama);
                          }
                          return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                        })()}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="text-xs overflow-x-none">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="text-left flex flex-col gap-1">
                        <span className="cursor-help font-bold text-lg">{b.nama}</span>
                        <p className="text-[10px] font-light whitespace-normal bg-stone-100 py-1 px-2 rounded-md w-fit border border-dashed border border-stone-300">
                          {(() => {
                            if (!b?.kode) return "Tidak ada detail";

                            const kodeParts = b.kode.split(".");
                            const path = [];
                            for (let i = 1; i <= kodeParts.length; i++) {
                              const prefix = kodeParts.slice(0, i).join(".");
                              const found = data.find((d) => d.kode === prefix);
                              if (found && found.nama) path.push(found.nama);
                            }
                            return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                          })()}
                        </p>
                      </TooltipTrigger>

                      <TooltipContent className="text-[10px] leading-relaxed">
                        {(() => {
                          if (!b?.kode) return "Tidak ada detail";

                          const kodeParts = b.kode.split(".");
                          const path = [];
                          for (let i = 1; i <= kodeParts.length; i++) {
                            const prefix = kodeParts.slice(0, i).join(".");
                            const found = data.find((d) => d.kode === prefix);
                            if (found && found.nama) path.push(found.nama);
                          }
                          return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                        })()}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    </div>
  );
}